import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import UserModal from '../components/users/UserModal';
import SubscriptionBadge from '../components/clients/SubscriptionBadge';
import { getClientDetail, getClientRequestDetail, getClientTickets } from '../services/clientService';
import { getSiteStats, getSiteVisits } from '../services/siteService';
import { updateUser } from '../services/userService';
import { getUserPreviewInfo } from '../services/previewService';
import DomainTab from '../components/domains/DomainTab';
import EmailsTab from '../components/clients/EmailsTab';
import { REQUEST_STATUS_LABELS } from '../services/creationRequestService';
import { formatShortDate, formatRelativeDate } from '../utils/relativeTime';
import PresenceDot from '../components/ui/PresenceDot';

const PORTAL_URL = process.env.REACT_APP_PORTAL_URL || 'https://portail.monsitedirect.fr';

const TICKET_STATUS_STYLES = {
  new: 'bg-warning/20 text-warning border border-warning/30',
  in_progress: 'bg-info/20 text-info border border-info/30',
  resolved: 'bg-success/20 text-success border border-success/30',
  closed: 'bg-base-300 text-base-content/60 border border-base-300'
};

const TICKET_STATUS_LABELS = {
  new: 'Nouveau',
  in_progress: 'En cours',
  resolved: 'Résolu',
  closed: 'Clos'
};

const TABS = [
  { id: 'compte', label: 'Compte & Abonnement' },
  { id: 'site', label: 'Site web' },
  { id: 'domaine', label: 'Domaine' },
  { id: 'creation', label: 'Création assistée' },
  // Journal des emails réellement envoyés (lot I2a, lecture seule) : placé après les onglets
  // métier et AVANT « Support », qui reste le dernier (DI2a-2).
  { id: 'emails', label: 'Emails' },
  { id: 'support', label: 'Support' }
];

const getClientName = (c) =>
  `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || c?.email || 'Client sans nom';

const Label = ({ children }) => (
  <p className="text-xs text-base-content/60 uppercase tracking-wide">{children}</p>
);

const Value = ({ children, className = '' }) => (
  <p className={`text-base-content mt-1 ${className}`}>{children || '—'}</p>
);

const ClientDetailPage = () => {
  const { uid } = useParams();
  const [activeTab, setActiveTab] = useState('compte');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const load = useCallback(async () => {
    if (!uid) return;
    setIsLoading(true);
    setIsError(false);
    try {
      const detail = await getClientDetail(uid);
      if (!detail) {
        setData(null);
        return;
      }
      const [request, tickets, stats, visits, previewInfo] = await Promise.all([
        getClientRequestDetail(uid),
        getClientTickets(uid),
        detail.client.site && detail.client.site.hasSite ? getSiteStats(uid, 'daily') : Promise.resolve([]),
        detail.client.site && detail.client.site.hasSite ? getSiteVisits(uid, 10) : Promise.resolve({ visits: [] }),
        getUserPreviewInfo(uid)
      ]);
      setData({
        client: detail.client,
        request,
        tickets,
        stats: stats || [],
        visits: (visits && visits.visits) || [],
        previewInfo: previewInfo || { previewUrl: null, previewedAt: null }
      });
    } catch (error) {
      console.error('ClientDetailPage: erreur de chargement', error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  const handleEditSave = async (userData) => {
    try {
      await updateUser(uid, userData);
      setIsEditOpen(false);
      await load();
    } catch (error) {
      console.error('ClientDetailPage: erreur de sauvegarde', error);
    }
  };

  const client = data?.client || null;
  const site = client?.site || null;
  const request = data?.request || null;
  const tickets = data?.tickets || [];
  const totalVisits = (data?.stats || []).reduce((sum, s) => sum + (s.total || 0), 0);

  // ---- Onglet « Compte & Abonnement » ----
  const renderCompteTab = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="font-semibold">Informations du compte</h4>
        <button onClick={() => setIsEditOpen(true)} className="btn btn-sm btn-outline">
          ✏️ Modifier les informations
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
          <Label>Type de compte</Label>
          <Value className="font-medium">{client?.isCompany ? 'Entreprise' : 'Particulier'}</Value>
          {client?.isCompany && client.companyName && (
            <>
              <Label><span className="mt-3 block">Entreprise</span></Label>
              <Value className="font-medium">{client.companyName}</Value>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div><Label>SIRET</Label><Value className="text-sm">{client.siretNumber}</Value></div>
                <div><Label>TVA</Label><Value className="text-sm">{client.vatNumber}</Value></div>
              </div>
            </>
          )}
        </div>

        <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
          <Label>Date d'inscription</Label>
          <Value className="font-medium">{formatShortDate(client?.createdAt)}</Value>
          <Label><span className="mt-3 block">Abonnement</span></Label>
          <div className="mt-1.5">
            <SubscriptionBadge plan={client?.subscriptionPlan} subscriptionStatus={client?.subscriptionStatus} />
          </div>
          <Label><span className="mt-3 block">Expiration</span></Label>
          <Value className="text-sm">{formatShortDate(client?.subscriptionExpiryDate)}</Value>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
          <Label>Contact</Label>
          <Value className="text-sm">{client?.email}</Value>
          <Value className="text-sm">{client?.phone}</Value>
          <Value className="text-sm">@{client?.username || client?.msdId || ''}</Value>
        </div>
        <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
          <Label>Adresse</Label>
          <Value className="text-sm">{client?.address1}</Value>
          <Value className="text-sm">{client?.city} {client?.postalCode}</Value>
          <Value className="text-sm">{client?.country}</Value>
        </div>
        <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
          <Label>Identifiant (uid)</Label>
          <Value className="text-xs font-mono break-all">{client?.id}</Value>
          <Label><span className="mt-3 block">Email de bienvenue</span></Label>
          <Value className="text-sm">{client?.welcomeEmailSent ? 'Envoyé' : 'Non envoyé'}</Value>
        </div>
      </div>
    </div>
  );

  // ---- Onglet « Site web » ----
  const renderSiteTab = () => {
    const previewInfo = data?.previewInfo || null;
    const previewedAt = previewInfo?.previewedAt ? new Date(previewInfo.previewedAt) : null;

    return (
      <div className="space-y-4">
        {/* Prévisualisation générée (deployment-service) */}
        <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
          <Label>Prévisualisation</Label>
          {previewInfo?.previewUrl ? (
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <a href={previewInfo.previewUrl} target="_blank" rel="noopener noreferrer"
                className="btn btn-sm btn-outline btn-info">
                👁 Voir la prévisualisation
              </a>
              <span
                className="text-sm text-base-content/60"
                title={previewedAt ? previewedAt.toLocaleString('fr-FR') : ''}
              >
                {previewedAt ? `Dernière prévisualisation : ${formatRelativeDate(previewedAt)}` : ''}
              </span>
            </div>
          ) : (
            <p className="text-sm text-base-content/60 mt-1">Aucune prévisualisation récente.</p>
          )}
        </div>

        {!site ? (
          <div className="text-center py-12 bg-base-200/30 rounded-lg border border-dashed border-base-300">
            <p className="text-base-content font-medium">Aucun site publié pour ce client.</p>
            <p className="text-sm text-base-content/60 mt-1">
              {client?.request && !client.request.trackingClosed
                ? 'Une création assistée est en cours : le site sera disponible à la livraison.'
                : 'Le client n\'a pas encore de site en ligne.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
                <Label>Domaine</Label>
                {site.domainName ? (
                  <a href={`https://${site.domainName}`} target="_blank" rel="noopener noreferrer"
                    className="text-info hover:underline font-medium break-all">
                    {site.domainName}
                  </a>
                ) : <Value>Non défini</Value>}
              </div>
              <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
                <Label>Statut</Label>
                <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs font-medium ${
                  site.domainName
                    ? 'bg-success/20 text-success border border-success/30'
                    : 'bg-warning/20 text-warning border border-warning/30'
                }`}>
                  {site.domainName ? 'En ligne' : 'Non publié'}
                </span>
              </div>
              <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
                <Label>Dernière publication</Label>
                <Value className="font-medium">{formatShortDate(site.publishedAt)}</Value>
              </div>
              <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
                <Label>Visites (30 derniers jours)</Label>
                <Value className="text-2xl font-bold text-primary">{totalVisits || 0}</Value>
              </div>
            </div>

            <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
              <Label>Dernières visites</Label>
              {data?.visits.length > 0 ? (
                <ul className="mt-2 divide-y divide-base-200">
                  {data.visits.map((visit, index) => (
                    <li key={visit.id || index}
                      className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="text-base-content/80">
                        {formatRelativeDate(visit.timestamp || visit.createdAt)}
                      </span>
                      <span className="text-base-content/60 truncate max-w-[220px]">
                        {visit.referrer || 'direct'}
                      </span>
                      <span className="text-xs text-base-content/50">{visit.language || 'N/A'}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-base-content/60 mt-2">Aucune visite enregistrée.</p>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // ---- Onglet « Création assistée » ----
  const renderCreationTab = () => {
    if (!request) {
      return (
        <div className="text-center py-12 bg-base-200/30 rounded-lg border border-dashed border-base-300">
          <p className="text-base-content font-medium">Aucune demande de création assistée.</p>
          <p className="text-sm text-base-content/60 mt-1">Ce client n'a pas utilisé le parcours « Faire créer mon site ».</p>
        </div>
      );
    }

    const messages = Array.isArray(request.messages) ? [...request.messages].reverse() : [];
    const previews = Array.isArray(request.previews) ? request.previews : [];

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              request.status === 'site_pret'
                ? 'bg-success/20 text-success border border-success/30'
                : 'bg-accent/20 text-accent border border-accent/30'
            }`}>
              {REQUEST_STATUS_LABELS[request.status] || request.status}
            </span>
            {request.trackingClosed && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-base-200 text-base-content/60 border border-base-200">
                Suivi terminé
              </span>
            )}
            {request.feedbackRating ? (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-warning/20 text-warning border border-warning/30">
                ⭐ {request.feedbackRating}/5
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/creation-requests?request=${encodeURIComponent(uid)}`} className="btn btn-sm btn-primary">
              Ouvrir cette demande
            </Link>
            <Link to="/creation-requests" className="btn btn-sm btn-outline">
              File « Demandes »
            </Link>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
            <Label>Demande envoyée le</Label>
            <Value className="font-medium">{formatShortDate(request.submittedAt)}</Value>
          </div>
          <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
            <Label>Dernière mise à jour</Label>
            <Value className="font-medium">{formatShortDate(request.updatedAt)}</Value>
          </div>
          <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
            <Label>Entreprise</Label>
            <Value className="font-medium">{request.businessInfo?.nomCommercial || '—'}</Value>
          </div>
        </div>

        {previews.length > 0 && (
          <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
            <Label>Prévisualisations de la demande</Label>
            <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {previews.map((preview, index) => (
                <a key={preview.id || index} href={preview.url} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-info hover:underline truncate">
                  {preview.label || `Prévisualisation ${index + 1}`}
                </a>
              ))}
            </div>
          </div>
        )}

        {messages.length > 0 && (
          <div className="bg-base-200/30 border border-base-200 rounded-lg p-4">
            <Label>Derniers échanges</Label>
            <ul className="mt-2 divide-y divide-base-200">
              {messages.slice(0, 10).map((message, index) => (
                <li key={message.timestamp?.toString?.() || index}
                  className="py-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-medium ${message.isStaff ? 'text-info' : 'text-base-content'}`}>
                      {message.isStaff ? 'Équipe MSD' : 'Client'}
                    </span>
                    <span className="text-xs text-base-content/50">
                      {message.timestamp ? formatRelativeDate(message.timestamp) : ''}
                    </span>
                  </div>
                  <p className="text-base-content/80 mt-0.5 whitespace-pre-wrap">{message.content}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // ---- Onglet « Support » ----
  const renderSupportTab = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4 className="font-semibold">Tickets de support ({tickets.length})</h4>
        <Link to="/support" className="btn btn-sm btn-outline">
          Ouvrir l'onglet Support
        </Link>
      </div>

      {tickets.length === 0 ? (
        <div className="text-center py-12 bg-base-200/30 rounded-lg border border-dashed border-base-300">
          <p className="text-base-content font-medium">Aucun ticket de support.</p>
        </div>
      ) : (
        <ul className="divide-y divide-base-200 border border-base-200 rounded-lg overflow-hidden bg-base-100">
          {tickets.map((ticket) => (
            <li key={ticket.id}>
              <Link
                to={`/support?userId=${encodeURIComponent(uid)}&ticketId=${ticket.id}`}
                className="p-4 flex flex-wrap items-center justify-between gap-2 hover:bg-base-200/50 transition-colors"
                title="Ouvrir ce ticket dans l'onglet Support"
              >
                <div className="min-w-0">
                  <p className="font-medium text-base-content truncate">{ticket.title}</p>
                  <p className="text-xs text-base-content/60 mt-0.5">
                    {ticket.category ? `${ticket.category} · ` : ''}mis à jour {formatRelativeDate(ticket.updatedAt)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${TICKET_STATUS_STYLES[ticket.status] || TICKET_STATUS_STYLES.new}`}>
                    {TICKET_STATUS_LABELS[ticket.status] || ticket.status}
                  </span>
                  <span className="text-xs text-base-content/40">Ouvrir →</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="py-16 text-center">
        <span className="loading loading-spinner loading-lg text-primary"></span>
        <p className="mt-4 text-base-content/70">Chargement de la fiche client...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="py-16 text-center">
        <p className="text-error font-medium">Erreur lors du chargement de la fiche client.</p>
        <button onClick={load} className="btn btn-outline btn-sm mt-4">Réessayer</button>
        <Link to="/clients" className="btn btn-ghost btn-sm mt-4 ml-2">Retour à la liste</Link>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="py-16 text-center">
        <p className="text-base-content font-medium">Client introuvable.</p>
        <Link to="/clients" className="btn btn-outline btn-sm mt-4">Retour à la liste</Link>
      </div>
    );
  }

  const initials = getClientName(client).replace(/[^a-zA-ZÀ-ÿ\s]/g, '')
    .split(/\s+/).slice(0, 2).map((p) => p[0] || '').join('').toUpperCase();

  const assistUrl = `${PORTAL_URL}/admin/assist?uid=${encodeURIComponent(uid)}`;
  const siteUrl = site && site.domainName ? `https://${site.domainName}` : null;

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Fil d'Ariane */}
      <div className="text-sm breadcrumbs text-base-content/60 mb-4">
        <ul>
          <li><Link to="/clients" className="hover:text-primary">Clients</Link></li>
          <li>{getClientName(client)}</li>
        </ul>
      </div>

      {/* En-tête */}
      <div className="bg-base-100 rounded-lg shadow-sm border border-base-200 p-5 mb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold text-lg">
              {initials}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold text-base-content">{getClientName(client)}</h1>
                <PresenceDot uid={uid} withLabel />
                <SubscriptionBadge plan={client.subscriptionPlan} subscriptionStatus={client.subscriptionStatus} />
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  client.isCompany
                    ? 'bg-info/20 text-info border border-info/30'
                    : 'bg-success/20 text-success border border-success/30'
                }`}>
                  {client.isCompany ? 'Entreprise' : 'Particulier'}
                </span>
              </div>
              <p className="text-sm text-base-content/70 mt-1">{client.email}</p>
              {client.isCompany && client.companyName && (
                <p className="text-sm text-base-content/60">{client.companyName}</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={assistUrl} target="_blank" rel="noopener noreferrer"
              className="btn btn-sm btn-outline" title="Agir comme ce client sur le portail">
              🛠 Mode assistance
            </a>
            {siteUrl && (
              <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline">
                🌐 Voir le site
              </a>
            )}
            <a href={`mailto:${client.email}`} className="btn btn-sm btn-outline" title={`Écrire à ${client.email}`}>
              ✉️ Email
            </a>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-base-100 rounded-lg shadow-sm border border-base-200">
        <div className="border-b border-base-200 overflow-x-auto">
          <nav className="flex px-2">
            {TABS.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-base-content/70 hover:text-base-content'
                }`}>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-5">
          {activeTab === 'compte' && renderCompteTab()}
          {activeTab === 'site' && renderSiteTab()}
          {activeTab === 'domaine' && <DomainTab uid={uid} client={client} />}
          {activeTab === 'creation' && renderCreationTab()}
          {/* L'onglet Emails lit SES PROPRES données au montage (DI2a-9) : ouvrir une fiche sans y
              aller ne coûte aucune lecture supplémentaire. */}
          {activeTab === 'emails' && <EmailsTab uid={uid} />}
          {activeTab === 'support' && renderSupportTab()}
        </div>
      </div>

      {/* Modale d'édition */}
      <UserModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSave={handleEditSave}
        user={client}
      />
    </div>
  );
};

export default ClientDetailPage;

